import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Zap, Target, TrendingUp, ShieldCheck } from "lucide-react";
import { Button, Card, Badge, ScoreRing, ProgressTrack } from "../components/kit";
import { useAuth } from "../context/AuthContext";

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const start = () => navigate(user ? "/onboarding" : "/register");

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="buktu-wrap" style={{ paddingTop: "var(--space-24)", paddingBottom: "var(--space-16)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: "var(--space-16)", alignItems: "center" }} className="buktu-hero-grid">
          <div className="buktu-rise">
            <Badge tone="accent" dot>Vitals · for business owners</Badge>
            <h1
              style={{
                font: "var(--type-hero)",
                fontSize: "clamp(38px, 6vw, 68px)",
                letterSpacing: "var(--tracking-tight)",
                margin: "var(--space-5) 0 var(--space-5)",
                maxWidth: 640,
              }}
              data-testid="home-headline"
            >
              Growth feels like chaos. Your numbers shouldn't.
            </h1>
            <p style={{ font: "var(--type-body)", fontSize: "clamp(16px, 2vw, 20px)", color: "var(--text-muted)", maxWidth: 560 }} data-testid="home-subheader">
              Vitals pulls real signals from the tools you already use — sales, leads, cash, delivery — into one clear, honest picture of how your business is actually doing. No spreadsheets. No guessing. Fifteen seconds a day.
            </p>
            <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-8)", flexWrap: "wrap" }}>
              <Button size="lg" data-testid="home-get-started-btn" onClick={start} iconRight={<ArrowRight size={18} />}>
                Get Started
              </Button>
              <Button size="lg" variant="secondary" data-testid="home-see-pricing-btn" onClick={() => navigate("/pricing")}>
                See pricing
              </Button>
            </div>
            <div style={{ display: "flex", gap: "var(--space-6)", marginTop: "var(--space-10)", flexWrap: "wrap", font: "var(--type-body-sm)", color: "var(--text-faint)" }}>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Check size={15} style={{ color: "var(--buktu-green)" }} /> Free for business owners</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Check size={15} style={{ color: "var(--buktu-green)" }} /> 25-question baseline</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Check size={15} style={{ color: "var(--buktu-green)" }} /> One honest health score</span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Check size={15} style={{ color: "var(--buktu-green)" }} /> Anonymous peer cohort</span>
            </div>
          </div>

          {/* Score preview card */}
          <div className="buktu-rise" style={{ animationDelay: "120ms" }} data-brand="vitals">
            <Card padding="var(--space-6)" style={{ boxShadow: "var(--shadow-lg)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-5)" }}>
                <ScoreRing value={72} size={116} subLabel="of 100" />
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className="buktu-eyebrow">Business health</span>
                  <h3 style={{ font: "var(--type-heading)" }}>Steady, building momentum</h3>
                  <span style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Up 6 points this month</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)", marginTop: "var(--space-6)" }}>
                {[
                  ["Financial Health", 78, "good"],
                  ["Market Strength", 64, "watch"],
                  ["Growth Readiness", 71, "good"],
                ].map(([label, v, tone]) => (
                  <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", font: "var(--type-label)", color: "var(--text-body)" }}>
                      <span>{label}</span>
                      <span style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-muted)" }}>{v}</span>
                    </div>
                    <ProgressTrack value={v} tone={tone} height={6} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Deep band: how it works, guided + adaptive */}
      <section data-surface="deep" style={{ background: "var(--buktu-green-dark)" }}>
        <div className="buktu-wrap" style={{ paddingTop: "var(--space-20)", paddingBottom: "var(--space-20)" }}>
          <span className="buktu-eyebrow" style={{ color: "var(--buktu-sage)" }}>How Vitals works</span>
          <h2 style={{ font: "var(--type-display)", fontSize: "clamp(28px, 4vw, 42px)", letterSpacing: "var(--tracking-tight)", color: "#fff", margin: "var(--space-4) 0 var(--space-10)", maxWidth: 720 }}>
            A guided start, then fifteen honest seconds a day.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--space-4)" }} className="buktu-3col">
            {[
              { icon: <Target size={22} />, t: "Answer 25, once", d: "A quick, quiz-like baseline across seven areas — finances, offer, leads, money model, growth. Defer up to half and finish within a week." },
              { icon: <Zap size={22} />, t: "Three taps a day", d: "The daily check-in rotates three questions from a twelve-question pool, weighted toward whatever you said is holding you back." },
              { icon: <TrendingUp size={22} />, t: "Watch the score move", d: "One Business Health Score, built from what you actually do — with streaks, badges, and an anonymous peer cohort." },
            ].map((s, i) => (
              <div key={s.t} style={{ padding: "var(--space-6)", borderRadius: "var(--radius-lg)", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }}>
                <div style={{ width: 46, height: 46, borderRadius: "var(--radius-md)", background: "rgba(163,201,192,0.18)", color: "var(--buktu-sage)", display: "grid", placeItems: "center", marginBottom: "var(--space-4)" }}>{s.icon}</div>
                <div style={{ font: "var(--type-data)", fontSize: 12, color: "var(--buktu-sage)", marginBottom: 8 }}>0{i + 1}</div>
                <h3 style={{ font: "var(--type-heading)", color: "#fff", marginBottom: "var(--space-2)" }}>{s.t}</h3>
                <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two products teaser */}
      <section className="buktu-wrap" style={{ paddingTop: "var(--space-20)", paddingBottom: "var(--space-8)" }}>
        <div style={{ maxWidth: 620, marginBottom: "var(--space-8)" }}>
          <span className="buktu-eyebrow">One engine, two front doors</span>
          <h2 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", marginTop: "var(--space-3)" }}>
            Built for owners. And for the coaches who serve them.
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }} className="buktu-2col">
          {[
            { mark: "vitals", name: "Vitals", line: "A daily habit that turns self-reported signals into one clear picture of your business — the tool that takes you from hustler to founder to CEO.", points: ["Free for every business owner", "Guided 25-question baseline", "Fifteen-second daily check-in", "Score, streaks & peer cohort"] },
            { mark: "catalyst", name: "Catalyst", line: "White-label engagement and benchmarking for business coaches — cohort visibility, real-data challenges, and reporting under your own brand.", points: ["Start free with up to 25 active members", "Anonymized cohort benchmarks", "Challenges on real activity", "Custom reporting & exports"] },
          ].map((v) => (
            <Card key={v.name} interactive padding="var(--space-8)" onClick={() => navigate("/solutions")} data-testid={`home-product-${v.name.toLowerCase()}`}>
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                  <img src={`/brand/${v.mark}-mark.svg`} alt="" style={{ width: 38, height: 38 }} />
                  <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 28, letterSpacing: "-0.03em", color: "var(--buktu-green)" }}>{v.name.toLowerCase()}</span>
                </div>
                <p style={{ font: "var(--type-body)", color: "var(--text-body)" }}>{v.line}</p>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
                  {v.points.map((p) => (
                    <li key={p} style={{ display: "flex", gap: "var(--space-3)", font: "var(--type-body-sm)", color: "var(--text-muted)" }}>
                      <ShieldCheck size={16} style={{ color: "var(--buktu-sage)", flexShrink: 0, marginTop: 2 }} />
                      {p}
                    </li>
                  ))}
                </ul>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, font: "var(--type-label)", color: "var(--text-brand)" }}>
                  Learn more <ArrowRight size={16} />
                </span>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="buktu-wrap" style={{ paddingTop: "var(--space-16)" }}>
        <Card tone="wash" padding="var(--space-12)" style={{ textAlign: "center" }}>
          <h2 style={{ font: "var(--type-title)", letterSpacing: "var(--tracking-snug)", maxWidth: 560, margin: "0 auto" }}>
            See where your business actually stands.
          </h2>
          <p style={{ font: "var(--type-body)", color: "var(--text-muted)", marginTop: "var(--space-3)", maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
            Vitals is free. The baseline takes a few minutes, and your score shows up the moment you finish.
          </p>
          <div style={{ marginTop: "var(--space-6)", display: "flex", justifyContent: "center" }}>
            <Button size="lg" data-testid="home-cta-btn" onClick={start} iconRight={<ArrowRight size={18} />}>Get Started</Button>
          </div>
        </Card>
      </section>

      <style>{`
        @media (max-width: 860px) {
          .buktu-hero-grid { grid-template-columns: 1fr !important; gap: var(--space-10) !important; }
          .buktu-3col { grid-template-columns: 1fr !important; }
          .buktu-2col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
